/** Land web helpers: data hooks, labels for statuses and conflicts, and small shared pieces. */
import type { ReactNode } from "react";
import { UNIT_LABELS, type Quantity } from "../../../contract";
import { formatDate, formatNumber } from "../../../web/lib/format";
import { plural, t } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { Chip, Icon } from "../../../web/ui/kit";
import type { Budget, RainUnit, WaterState } from "../shared/grazing";
import { WATER_STATES } from "../contract";
import type { Conflict, MapData, Overview, PlaceGrazing, PlanView, StageStatus } from "../shared/types";

export const useOverview = () => useApi<Overview>("/m/land/overview");
export const usePlaceGrazing = (id: string | null | undefined) => useApi<PlaceGrazing>(id ? `/m/land/places/${id}` : null);
export const usePlan = (id: string | null | undefined) => useApi<PlanView>(id ? `/m/land/plans/${id}` : null);
export const useMapData = () => useApi<MapData>("/m/land/map");

export function rainText(amount: number | null | undefined, unit: RainUnit): string {
  return `${formatNumber(amount ?? 0, unit === "mm" ? 0 : 2)} ${t(UNIT_LABELS[unit] ?? unit)}`;
}

export function quantityText(q: Quantity | null | undefined): string {
  if (!q) return "";
  return `${formatNumber(q.value, q.value >= 100 ? 0 : 1)} ${t(UNIT_LABELS[q.unit] ?? q.unit)}`;
}

export function daysText(n: number): string {
  return plural(n, "{{count}} day", "{{count}} days");
}

export const STAGE_LABELS: Record<StageStatus, string> = {
  planned: "Planned", due: "Move due", grazing: "Grazing now", done: "Done", missed: "Not moved", skipped: "Step skipped",
};
export const STAGE_TONES: Record<StageStatus, "neutral" | "warn" | "land" | "good" | "bad"> = {
  planned: "neutral", due: "warn", grazing: "land", done: "good", missed: "bad", skipped: "neutral",
};

export function StageChip({ status }: { status: StageStatus }) {
  return <Chip tone={STAGE_TONES[status]} icon={status === "done" ? "done" : status === "due" ? "clock" : status === "grazing" ? "grass" : status === "missed" ? "warn" : undefined}>{t(STAGE_LABELS[status])}</Chip>;
}

export function conflictText(c: Conflict): string {
  switch (c.kind) {
    case "short_rest": return t("Only {{rest}} of {{target}} days of rest", { rest: c.rest, target: c.target });
    case "occupied": return plural(c.head, "{{count}} head are there now", "{{count}} head are there now");
    case "overlap": return t("Also planned in {{plan}}", { plan: c.plan.label });
    case "keep_off": return t("Keep cattle off until {{date}}", { date: formatDate(c.until) });
    case "water": return t("Water reported {{state}} {{date}}", { state: t(WATER_STATES[c.state]).toLowerCase(), date: formatDate(c.on) });
    case "capacity": return t("{{head}} head; it holds {{capacity}}", { head: c.head, capacity: c.capacity });
  }
}

export function ConflictList({ conflicts }: { conflicts: Conflict[] }) {
  if (!conflicts.length) return null;
  return (
    <ul className="land-conflicts">
      {conflicts.map((c, i) => <li key={i}><Icon name="warn" size={15} />{conflictText(c)}</li>)}
    </ul>
  );
}

export const WATER_TONE: Record<WaterState, "good" | "warn" | "bad"> = { ok: "good", low: "warn", empty: "bad", broken: "bad" };

export function WaterChip({ state }: { state: WaterState }) {
  return <Chip tone={WATER_TONE[state]} icon="water">{t(WATER_STATES[state])}</Chip>;
}

/** A forage budget in plain words, always marked as an estimate with what it assumes. */
export function BudgetNote({ budget, units = "imperial" }: { budget: Budget; units?: "imperial" | "metric" }) {
  if (!budget.ok) {
    const why: Record<NonNullable<Budget["reason"]>, string> = {
      no_forage: "Add a forage check with a pounds-per-acre estimate to see how long the grass will last.",
      no_area: "Add the pasture's acres, or draw it on the map, to see how long the grass will last.",
      no_animals: "No cattle to plan for.",
      nothing_left: "There's no forage above what you want to leave standing.",
      units: "That forage estimate is in units Hondo can't use for a budget.",
    };
    return <p className="muted land-budget">{t(why[budget.reason ?? "no_forage"])}</p>;
  }
  const a = budget.assumptions;
  const mass = units === "metric" && a.mass_lb_per_ac ? `${formatNumber(a.mass_lb_per_ac / 0.892179, 0)} ${t("kg/ha")}` : `${formatNumber(a.mass_lb_per_ac ?? 0, 0)} ${t("lb/ac")}`;
  const lines: ReactNode[] = [
    budget.days_supported !== undefined
      ? <strong key="d">{t("About {{days}} days of grass for these cattle", { days: formatNumber(budget.days_supported, 0) })}</strong>
      : <strong key="d">{t("About {{aud}} animal-unit days of grass", { aud: formatNumber(budget.available_aud ?? 0, 0) })}</strong>,
  ];
  return (
    <div className={budget.stale ? "land-budget is-stale" : "land-budget"}>
      <span className="eyebrow">{t("Estimate")}</span>
      <p>{lines}</p>
      <p className="muted">
        {[
          t("{{mass}} seen {{date}}", { mass, date: a.observed_on ? formatDate(a.observed_on) : "" }),
          a.residual_lb_per_ac !== undefined ? t("leaving {{amount}}", { amount: `${formatNumber(a.residual_lb_per_ac, 0)} ${t("lb/ac")}` }) : t("using {{pct}}% of it", { pct: a.utilization_pct ?? 50 }),
          a.area_ac !== undefined ? t("{{acres}} ac", { acres: formatNumber(a.area_ac, 0) }) : null,
          t("{{lb}} lb a day per animal unit", { lb: formatNumber(a.intake_lb, 0) }),
        ].filter(Boolean).join(" · ")}
      </p>
      {budget.needed_aud !== undefined && budget.available_aud !== undefined && <p className="muted">{t("Planned use {{needed}} of {{available}} animal-unit days", { needed: formatNumber(budget.needed_aud, 0), available: formatNumber(budget.available_aud, 0) })}</p>}
      {budget.stale && <p className="land-stale"><Icon name="clock" size={14} />{plural(budget.age_days ?? 0, "Forage check is {{count}} day old", "Forage check is {{count}} days old")}</p>}
    </div>
  );
}

export const GRAZED = new Set(["pasture", "trap", "field"]);
